import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import { MipsSubmissionBlockService } from '@services/mips-submission-block.service';
import { ErrorDialogService } from '@services/error-dialog.service';
import { MipsSubmissionBlockActions } from '@store/actions/mips-submission-block/mips-submission-block.actions';
import { parseProblemDetailsError } from '../../../utils/api-error.util';

@Injectable()
export class MipsSubmissionBlockEffects {
  loadOrganizationStatuses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsSubmissionBlockActions.loadOrganizationStatuses),
      switchMap(() =>
        this.mipsSubmissionBlockService.getOrganizationStatuses().pipe(
          map((statuses) => MipsSubmissionBlockActions.loadOrganizationStatusesSuccess({ statuses })),
          catchError((error: HttpErrorResponse) =>
            of(MipsSubmissionBlockActions.loadOrganizationStatusesFailure({
              error: parseProblemDetailsError(error, 'Failed to load MIPS submission statuses'),
            }))
          )
        )
      )
    )
  );

  loadOrganizationStatusesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MipsSubmissionBlockActions.loadOrganizationStatusesFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to load MIPS submission statuses. Please try again.',
            'MIPS Submission Error'
          );
        })
      ),
    { dispatch: false }
  );

  blockOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsSubmissionBlockActions.blockOrganization),
      switchMap(({ request }) =>
        this.mipsSubmissionBlockService.blockOrganization(request).pipe(
          map((status) => MipsSubmissionBlockActions.blockOrganizationSuccess({ status })),
          catchError((error: HttpErrorResponse) =>
            of(MipsSubmissionBlockActions.blockOrganizationFailure({
              error: parseProblemDetailsError(error, 'Failed to block organization'),
            }))
          )
        )
      )
    )
  );

  blockOrganizationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MipsSubmissionBlockActions.blockOrganizationFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to block organization. Please try again.',
            'Block Organization Error'
          );
        })
      ),
    { dispatch: false }
  );

  unblockOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsSubmissionBlockActions.unblockOrganization),
      switchMap(({ organizationId }) =>
        this.mipsSubmissionBlockService.unblockOrganization(organizationId).pipe(
          map(() => MipsSubmissionBlockActions.unblockOrganizationSuccess({ organizationId })),
          catchError((error: HttpErrorResponse) =>
            of(MipsSubmissionBlockActions.unblockOrganizationFailure({
              error: parseProblemDetailsError(error, 'Failed to unblock organization'),
            }))
          )
        )
      )
    )
  );

  unblockOrganizationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MipsSubmissionBlockActions.unblockOrganizationFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to unblock organization. Please try again.',
            'Unblock Organization Error'
          );
        })
      ),
    { dispatch: false }
  );

  bulkBlockOrganizations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MipsSubmissionBlockActions.bulkBlockOrganizations),
      switchMap(({ request }) =>
        this.mipsSubmissionBlockService.bulkBlockOrganizations(request).pipe(
          map((statuses) => MipsSubmissionBlockActions.bulkBlockOrganizationsSuccess({ statuses })),
          catchError((error: HttpErrorResponse) =>
            of(MipsSubmissionBlockActions.bulkBlockOrganizationsFailure({
              error: parseProblemDetailsError(error, 'Failed to bulk block organizations'),
            }))
          )
        )
      )
    )
  );

  bulkBlockOrganizationsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MipsSubmissionBlockActions.bulkBlockOrganizationsFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to bulk block organizations. Please try again.',
            'Bulk Block Error'
          );
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private mipsSubmissionBlockService: MipsSubmissionBlockService,
    private errorDialogService: ErrorDialogService,
  ) {}
}
